import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import OrderMiniMap from "@/components/OrderMiniMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Package, Users, Tag, Truck, FileText, CreditCard, Settings as SettingsIcon,
  Plus, Trash2, Check, AlertTriangle, Download, Pencil, Repeat, Pause, Play, Mail, MessageSquare, QrCode, Scale, History, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "clients", label: "Clients", icon: Users },
  { key: "pricing", label: "Price list", icon: Tag },
  { key: "drivers", label: "Drivers & jobs", icon: Truck },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "account", label: "Settings", icon: SettingsIcon },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};


function Recurring() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", weekday: "0", pickup_slot: "", notes: "", lines: [] });
  const load = useCallback(() => {
    api.get("/business/recurring").then((r) => { setRows(r.data); setLoaded(true); });
    api.get("/business/clients").then((r) => setClients(r.data));
    api.get("/business/items").then((r) => setItems(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addLine = (it) => setForm((f) => {
    const ex = f.lines.find((l) => l.item_id === it.id);
    if (ex) return { ...f, lines: f.lines.map((l) => l.item_id === it.id ? { ...l, qty: l.qty + 1 } : l) };
    return { ...f, lines: [...f.lines, { item_id: it.id, name: it.name, price: it.price, qty: 1 }] };
  });
  const setQty = (id, qty) => setForm((f) => ({ ...f, lines: f.lines.map((l) => l.item_id === id ? { ...l, qty: Math.max(1, qty) } : l) }));

  const create = async () => {
    if (!form.client_id) return toast.error("Choose a client");
    if (!form.lines.length) return toast.error("Add at least one item");
    try {
      await api.post("/business/recurring", { client_id: form.client_id, items: form.lines,
        weekday: Number(form.weekday), pickup_slot: form.pickup_slot, notes: form.notes });
      toast.success("Standing order created"); setOpen(false);
      setForm({ client_id: "", weekday: "0", pickup_slot: "", notes: "", lines: [] }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (r) => { await api.put(`/business/recurring/${r.id}`, { active: !r.active }); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this standing order?")) return; await api.delete(`/business/recurring/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Recurring orders</h1>
          <p className="mt-1 text-sm text-slate-500">Standing weekly orders auto-generate on their day — set it once for regulars like hotels.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-recurring-btn"><Plus className="mr-1 h-4 w-4" /> New standing order</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>New standing order</DialogTitle><DialogDescription>Repeats automatically every week on the chosen day.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Client</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                    <SelectTrigger data-testid="recurring-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Repeats every</Label>
                  <Select value={form.weekday} onValueChange={(v) => setForm((f) => ({ ...f, weekday: v }))}>
                    <SelectTrigger data-testid="recurring-weekday-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Pickup slot</Label><Input placeholder="09:00-11:00" value={form.pickup_slot} onChange={(e) => setForm((f) => ({ ...f, pickup_slot: e.target.value }))} data-testid="recurring-slot" /></div>
              <div>
                <Label>Items</Label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {items.map((it) => <button key={it.id} onClick={() => addLine(it)} className="rounded-full border border-slate-200 px-3 py-1 text-xs hover:border-brand hover:text-brand" data-testid={`recurring-add-item-${it.id}`}>+ {it.name} ({money(it.price)})</button>)}
                </div>
                <div className="mt-3 space-y-2">
                  {form.lines.map((l) => (
                    <div key={l.item_id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                      <span>{l.name}</span>
                      <div className="flex items-center gap-2"><Input type="number" min="1" value={l.qty} onChange={(e) => setQty(l.item_id, Number(e.target.value))} className="h-8 w-16" /><span className="w-16 text-right font-medium">{money(l.price * l.qty)}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="recurring-create-btn">Create standing order</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r) => (
          <div key={r.id} className={`rounded-2xl border bg-white p-5 ${r.active ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`recurring-card-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading font-bold text-slate-900">{r.client_name}</p>
                <p className="text-sm text-slate-500">Every <b>{r.weekday_label}</b> · {r.pickup_slot || "any time"}</p>
              </div>
              <Badge className={`border-0 ${r.active ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-600"}`}>{r.active ? "Active" : "Paused"}</Badge>
            </div>
            <p className="mt-2 text-xs text-slate-400">{r.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
            <p className="mt-1 text-sm font-medium">{money(r.total)} / week</p>
            <p className="mt-1 text-xs text-slate-400">Next run: {r.next_run}</p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => toggle(r)} data-testid={`recurring-toggle-${r.id}`}>{r.active ? <><Pause className="mr-1 h-3 w-3" /> Pause</> : <><Play className="mr-1 h-3 w-3" /> Resume</>}</Button>
              <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-red-500" data-testid={`recurring-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {!rows.length && <p className="text-sm text-slate-400">{loaded ? "No standing orders yet." : "Loading…"}</p>}
      </div>
    </div>
  );
}


/* -------------------------------- Clients -------------------------------- */

export default Recurring;
